import { DateTime } from 'luxon';

import {
  ALL_PORTFOLIO_USERS,
  PORTFOLIO_USER_ROLE_ADMIN,
  PORTFOLIO_USER_ROLE_OWNER,
  PORTFOLIO_USER_ROLE_STANDARD,
  PORTFOLIO_USER_ROLE_VIEWER,
  TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL,
  TRADE_POINT_TYPE_COMMUNITY, TRADE_POINT_TYPE_KNOWN,
  TRADE_POINT_TYPE_RESIDUAL,
} from '../constants';

/**
 * Takes in a timestamp and returns the formatted i18n date string
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {number} timestamp
 * @returns {string} - date string
 */
//  Note: This assumes the browser local timezone not the property timezone
export const getStringAndNumericFormattedDate = (intl, timestamp) => {
  if (!intl || !timestamp) {
    return null;
  }

  return intl.formatDate(DateTime.fromSeconds(timestamp), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats a Unix timestamp with the application's selected locale.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {number} timestamp - Unix timestamp in seconds
 * @returns {string | null} Full localized date and time string
 */
export const formatDateTimeFull = (intl, timestamp) => {
  if (!intl || timestamp === null || timestamp === undefined) {
    return null;
  }

  return DateTime.fromSeconds(timestamp)
    .setLocale(intl.locale)
    .toLocaleString(DateTime.DATETIME_FULL);
};

/**
 * Takes in a date object and returns the locale based date
 * and time string in the provided timezone.
 * @param {string} locale - date object
 * @param {string} tz - time zone of the property
 * @param {object} timestamp - date object
 * @returns {string} - date and time formatted string
 */
export const getDateAndTimeFormattedString = (locale, tz, timestamp) => {
  if (!timestamp || !locale || !tz) {
    return null;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    timeStyle: 'long',
    timeZone: tz,
  }).format(timestamp);
};

/**
 * Takes in a key and the intl object and returns the translated key.
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {ALL_PORTFOLIO_USERS | PORTFOLIO_USER_ROLE_OWNER
 * | PORTFOLIO_USER_ROLE_ADMIN | PORTFOLIO_USER_ROLE_STANDARD
 * |PORTFOLIO_USER_ROLE_VIEWER } role - Portfolio user role.
 * @returns {(string | null)} - translated user role, or null if not a valid role
 * to translate or no intl present.
 */
export const getPortfolioUserRoleLabel = (intl, role) => {
  if (!role || !intl) {
    return null;
  }

  switch (role) {
    case PORTFOLIO_USER_ROLE_OWNER:
      return intl.formatMessage({ id: 'util.i18n.helpers.get_portfolio_user_role_label.owner.label', defaultMessage: 'Owner' });

    case PORTFOLIO_USER_ROLE_ADMIN:
      return intl.formatMessage({ id: 'util.i18n.helpers.get_portfolio_user_role_label.administrator.label', defaultMessage: 'Administrator' });

    case PORTFOLIO_USER_ROLE_STANDARD:
      return intl.formatMessage({ id: 'util.i18n.helpers.get_portfolio_user_role_label.standard_user.label', defaultMessage: 'Standard User' });

    case PORTFOLIO_USER_ROLE_VIEWER:
      return intl.formatMessage({ id: 'util.i18n.helpers.get_portfolio_user_role_label.viewer.label', defaultMessage: 'Viewer' });

    default:
      return intl.formatMessage({ id: 'util.i18n.helpers.get_portfolio_user_role_label.unspecified.label', defaultMessage: 'Unspecified' });
  }
};

/**
 * Returns the localized trade direction label in imperative form (Buy/Sell).
 * @param {import('react-intl').IntlShape} intl - The react-intl internationalization object
 * @param {string} direction - The trade direction value from GraphQL TradeDirection enum.
 *   Should be one of TRADE_DIRECTION_BUY or TRADE_DIRECTION_SELL as defined in the GraphQL schema.
 * @returns {string|null} The localized trade direction label
 */
export const getTradeDirection = (intl, direction) => {
  if (!direction || !intl) {
    return null;
  }

  switch (direction) {
    case TRADE_DIRECTION_BUY:
      return intl.formatMessage({ id: 'util.i18n.get_trade_direction.buy.label', defaultMessage: 'Buy' });

    case TRADE_DIRECTION_SELL:
      return intl.formatMessage({ id: 'util.i18n.get_trade_direction.sell.label', defaultMessage: 'Sell' });

    default:
      return intl.formatMessage({ id: 'util.i18n.get_trade_direction.unspecified.label', defaultMessage: 'Trade direction unspecified' });
  }
};

/**
 * Returns the localized trade direction label in gerund/action form (Buying/Selling).
 * @param {import('react-intl').IntlShape} intl - The react-intl internationalization object
 * @param {string} direction - The trade direction value from GraphQL TradeDirection enum.
 *   Should be one of TRADE_DIRECTION_BUY or TRADE_DIRECTION_SELL as defined in the GraphQL schema.
 * @returns {string|null} The localized trade direction action label
 */
export const getTradeDirectionAction = (intl, direction) => {
  if (!direction || !intl) {
    return null;
  }

  switch (direction) {
    case TRADE_DIRECTION_BUY:
      return intl.formatMessage({ id: 'util.i18n.get_trade_direction_action.buy.label', defaultMessage: 'Buying' });

    case TRADE_DIRECTION_SELL:
      return intl.formatMessage({ id: 'util.i18n.get_trade_direction_action.sell.label', defaultMessage: 'Selling' });

    default:
      return intl.formatMessage({ id: 'util.i18n.get_trade_direction_action.unspecified.label', defaultMessage: 'Trade direction unspecified' });
  }
};

/**
 * Returns the localized trade direction label in gerund/action form (Buying/Selling).
 * @param {import('react-intl').IntlShape} intl - The react-intl internationalization object
 * @param {string} direction - The trade direction value from GraphQL TradeDirection enum.
 *   Should be one of TRADE_DIRECTION_BUY or TRADE_DIRECTION_SELL as defined in the GraphQL schema.
 * @returns {string|null} The localized trade direction action label
 */
export const getTradeDirectionActionAndPreposition = (intl, direction) => {
  if (!direction || !intl) {
    return null;
  }

  switch (direction) {
    case TRADE_DIRECTION_BUY:
      return intl.formatMessage({ id: 'util.i18n.get_trade_direction_action_and_preposition.buy.label', defaultMessage: 'Buying for' });

    case TRADE_DIRECTION_SELL:
      return intl.formatMessage({ id: 'util.i18n.get_trade_direction_action_and_preposition.sell.label', defaultMessage: 'Selling from' });

    default:
      return intl.formatMessage({ id: 'util.i18n.get_trade_direction_action_and_preposition.unspecified.label', defaultMessage: 'Trade direction unspecified' });
  }
};

/**
 * Takes in a trade point type and the intl object and returns the translated label
 * for the trade point type
 * @param {import('react-intl').IntlShape} intl - i18n intl object
 * @param {TRADE_POINT_TYPE_KNOWN |
 * TRADE_POINT_TYPE_COMMUNITY |
 * TRADE_POINT_TYPE_RESIDUAL} type - trade point type
 * @returns {(string | null)} - translated label, or null if not a valid type
 *                              to translate or no intl present.
 */
export const getTradePointTypeLabel = (intl, type) => {
  if (!type || !intl) {
    return null;
  }

  switch (type) {
    case TRADE_POINT_TYPE_KNOWN:
      return intl.formatMessage({ id: 'util.i18n.helpers.trade_point.type.known.label', defaultMessage: 'Trade Point' });

    case TRADE_POINT_TYPE_COMMUNITY:
      return intl.formatMessage({ id: 'util.i18n.helpers.trade_point.type.community.label', defaultMessage: 'Community' });

    case TRADE_POINT_TYPE_RESIDUAL:
      return intl.formatMessage({ id: 'util.i18n.helpers.trade_point.type.residual.label', defaultMessage: 'Retailer' });

    default:
      return type;
  }
};
